/** 解析接口/库里的 JSON 字段（字符串或对象） */
export function parseJsonValue(input) {
  if (input === null || input === undefined || input === '') return null;
  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!trimmed) return null;
    try {
      return JSON.parse(trimmed);
    } catch (e) {
      return trimmed;
    }
  }
  return input;
}

export function isPlainObject(val) {
  return val !== null && typeof val === 'object' && !Array.isArray(val);
}

/** 是否适合用「对象数组」表格展示 */
export function isTableArray(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return false;
  return arr.every((item) => isPlainObject(item));
}

/** 是否为单行对象（适合 key:value 双列表单，而非表格） */
export function isSingleRowKeyValueData(data) {
  const v = parseJsonValue(data);
  if (isPlainObject(v)) return Object.keys(v).length > 0;
  if (Array.isArray(v) && v.length === 1 && isPlainObject(v[0])) return true;
  return false;
}

/** 从单行/单对象结构取出展示用对象 */
export function extractSingleRowObject(data) {
  const v = parseJsonValue(data);
  if (isPlainObject(v)) return v;
  if (Array.isArray(v) && v.length === 1 && isPlainObject(v[0])) return v[0];
  return null;
}

/** 按优先字段排序后生成 { key, val } 列表 */
export function buildKeyValueEntries(obj, priorityKeys = []) {
  if (!isPlainObject(obj)) return [];
  const keys = Object.keys(obj);
  const ordered = [];
  (priorityKeys || []).forEach((k) => {
    if (keys.includes(k) && !ordered.includes(k)) ordered.push(k);
  });
  keys.forEach((k) => {
    if (!ordered.includes(k)) ordered.push(k);
  });
  return ordered.map((key) => ({ key, val: obj[key] }));
}

export function collectColumnKeys(rows) {
  const keys = new Set();
  rows.forEach((row) => {
    Object.keys(row || {}).forEach((k) => keys.add(k));
  });
  return Array.from(keys);
}

export function isNestedCellValue(val) {
  if (val === null || val === undefined) return false;
  if (Array.isArray(val)) return val.length > 0;
  if (isPlainObject(val)) return Object.keys(val).length > 0;
  return false;
}

export function formatPrimitive(val) {
  if (val === null || val === undefined || val === '') return '-';
  if (typeof val === 'boolean') return val ? '是' : '否';
  return String(val);
}

/** 资产 PO 常见字段中文名 */
export const ASSET_PO_FIELD_LABELS = {
  opTicketid: 'PO单号',
  po_ticket_id: '腾讯侧PO单号',
  dcops_ticket_id: 'DCOPS单号',
  idc_name: '机房名称',
  campus_name: '园区',
  task_name: '任务名称',
  task_desc: '任务描述',
  ticket_info: '工单信息',
  bill_info: '账单信息',
  resp_data: '响应数据',
  assets_data: '资产数据',
  check_memo: '确认信息',
  check_remark: '备注',
  bill_weight: '计费权重',
  is_expired: '是否超时',
  expire_time: '超时时间',
  dispatch_time: '派单时间',
  finished_time: '完成时间',
  current_user: '处理人',
  current_phone: '联系电话',
  by_quantity_num: '按量件数',
  by_the_piece_num: '计件件数',
  ticket_instance_name: '流程标识',
  completer: '结单人',
  PN: 'PN',
  SN: 'SN',
  count: '数量',
  seq_id: '序号',
  enabled: '是否启用',
  category: '类别',
  asset_type: '资产类型',
  manage_mode: '管理模式',
  real_num: '实际数量',
  item_name: '计费项',
  idcName_hy: '鸿云机房',
  idcp_name: '机房',
  ticket_status: '工单状态',
  planCount: '计划数量',
  respCode: '响应码',
  respDesc: '响应说明',
  callId: '呼叫ID',
};

export function fieldLabel(key, customLabels = {}) {
  const map = { ...ASSET_PO_FIELD_LABELS, ...customLabels };
  return map[key] || key;
}

/** 资产明细表常见列的最小宽度（px） */
const COLUMN_WIDTH_FLOOR = {
  task_desc: 300,
  idc_name: 240,
  ticket_instance_name: 160,
  task_name: 120,
  SN: 220,
  PN: 180,
  asset_type: 150,
  manage_mode: 110,
  category: 100,
  enabled: 90,
  seq_id: 80,
  count: 80,
  check_memo: 100,
  check_remark: 160,
  dcops_ticket_id: 160,
  assets_data: 800,
  item_name: 200,
  idcName_hy: 160,
  real_num: 90,
  planCount: 90,
  strategy: 280,
  eventDescription: 200,
};

const ASSET_DETAIL_KEYS = ['SN', 'PN', 'asset_type', 'seq_id', 'category', 'manage_mode'];

/** 是否为资产明细子表（PN/SN 等） */
export function isAssetDetailTable(rows) {
  if (!rows || !rows.length) return false;
  const keys = collectColumnKeys(rows);
  return ASSET_DETAIL_KEYS.filter((k) => keys.includes(k)).length >= 2;
}

function measureTextPx(text) {
  const s = String(text ?? '');
  let w = 28;
  for (let i = 0; i < s.length; i += 1) {
    w += s.charCodeAt(i) > 255 ? 15 : 9;
  }
  return w;
}

/**
 * 按表头中文名 + 样本行内容估算列最小宽度
 */
export function calcColumnMinWidth(col, rows = [], options = {}) {
  const { isAssetDetail = false } = options;
  const floor = COLUMN_WIDTH_FLOOR[col] || (isAssetDetail ? 120 : 100);
  const labelPx = measureTextPx(fieldLabel(col));
  let contentPx = 0;
  const sample = Array.isArray(rows) ? rows.slice(0, 40) : [];
  sample.forEach((row) => {
    const v = row?.[col];
    if (v === null || v === undefined || typeof v === 'object') return;
    contentPx = Math.max(contentPx, measureTextPx(formatPrimitive(v)));
  });
  const width = Math.max(floor, labelPx, contentPx);
  return Math.min(width, isAssetDetail ? 520 : 420);
}

/** 资产数据嵌套表总宽度（用于外层 assets_data 列） */
export function calcAssetsDataColumnWidth(rows) {
  const parsedRows = rows
    .map((row) => {
      const raw = row?.assets_data;
      const val = parseJsonValue(raw);
      return isTableArray(val) ? val : null;
    })
    .find((arr) => arr && arr.length);
  if (!parsedRows) return COLUMN_WIDTH_FLOOR.assets_data;
  const cols = collectColumnKeys(parsedRows);
  const sum = cols.reduce(
    (total, col) => total + calcColumnMinWidth(col, parsedRows, { isAssetDetail: true }),
    48
  );
  return Math.min(Math.max(sum, COLUMN_WIDTH_FLOOR.assets_data), 1600);
}
